import { createSignal, ReadonlySignal } from '@funtech-int/rival';
import { channelListQuery } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { GraphqlClient } from '@/shared/lib/graphqlClient';

import { CHANNEL_COUNT_FOR_WALLPAPER } from '../constants';

import { getChannelsWallpapers, ChannelWallpaperItem } from './utils/getChannelsWallpapers';

interface ChannelWallpaperManagerParams {
    graphqlClient: GraphqlClient;
}

export interface ChannelWallpaperManager {
    channelsWallpapers: ReadonlySignal<ChannelWallpaperItem[]>;
    loading: ReadonlySignal<boolean>;
    loadChannels(categoryIds?: number[]): Promise<void>;
}

export function createChannelWallpaperManager({
    graphqlClient,
}: ChannelWallpaperManagerParams): ChannelWallpaperManager {
    const CHANNELS_LIMIT = CHANNEL_COUNT_FOR_WALLPAPER;

    const channelsWallpapersSignal = createSignal<ChannelWallpaperItem[]>([]);
    const loadingSignal = createSignal<boolean>(false);

    async function fetchChannels(categoryIds: number[] = []): Promise<ChannelWallpaperItem[]> {
        const result = await graphqlClient.fetch({
            operation: channelListQuery,
            variables: {
                categoryIds,
                limit: CHANNELS_LIMIT,
                offset: 0,
                withPrograms: false,
            },
        });

        if (!result.data?.channels) {
            return [];
        }

        const channelsWallpapers = getChannelsWallpapers(result.data.channels);

        return channelsWallpapers;
    }

    async function loadChannels(categoryIds: number[] = []): Promise<void> {
        const existingChannels = channelsWallpapersSignal.get();

        if (existingChannels.length > 0) {
            return;
        }

        loadingSignal.set(true);

        try {
            const channelsWallpapers = await fetchChannels(categoryIds);
            channelsWallpapersSignal.set(channelsWallpapers);
        } finally {
            loadingSignal.set(false);
        }
    }

    return {
        channelsWallpapers: channelsWallpapersSignal.seal(),
        loading: loadingSignal.seal(),
        loadChannels,
    };
}
